import React, { useEffect, useState } from "react";
import { Card, Table, Badge, Alert, Spinner } from "react-bootstrap";
import { fetchManutentionsAConfirmer } from "./DashboardService";
import IconShow from "../../images/fi_eye.png";

/**
 * Manutentions laissées à 0 faute de chiffrage.
 *
 * Tant qu'une manutention n'est pas confirmée, son supplément vaut 0, et c'est
 * ce 0 qui part sur la facture mensuelle. Une fois la facture émise, l'API
 * refuse la confirmation : la prestation est définitivement offerte.
 *
 * L'urgence suit donc la fin du mois et non l'âge de la commande — une
 * manutention en attente depuis le 3 n'est pas urgente le 4, elle est critique
 * le 30.
 */

const SEUIL_CRITIQUE = 5;
const SEUIL_ATTENTION = 10;

const LIBELLES = {
  distance: {
    "0-5": "0-5 m", "5-15": "5-15 m", "15-30": "15-30 m",
    "30-50": "30-50 m", "50+": "plus de 50 m",
  },
  obstacles: {
    aucun: "sans obstacle", quelques: "quelques obstacles", difficile: "accès difficile",
  },
  etage: {
    non_concerne: "plain-pied", rdc_surleve: "RDC surélevé", "1er": "1er étage", "2e_plus": "2e étage ou plus",
  },
  ascenseur: { oui: "avec ascenseur", non: "sans ascenseur" },
  poids_colis: { normal: "poids normal", lourd: "colis lourd" },
};

/** Résume les réponses déjà saisies, pour rappeler le client sans rouvrir la commande. */
const resumerReponses = (reponses) => {
  if (!reponses) return null;
  const parts = Object.entries(LIBELLES)
    .map(([champ, table]) => table[reponses[champ]])
    .filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
};

const ManutentionsAConfirmer = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const charger = async () => {
      try {
        setError(null);
        setData(await fetchManutentionsAConfirmer());
      } catch (e) {
        console.error("Erreur chargement manutentions à confirmer:", e);
        setError("Impossible de charger les manutentions à confirmer");
      } finally {
        setLoading(false);
      }
    };

    charger();
    const interval = setInterval(charger, 60000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  if (error) {
    return <Alert variant="warning">{error}</Alert>;
  }

  // Rien en attente : le carton disparaît entièrement. Un bloc qui répète
  // « rien à faire » finit par ne plus être lu le jour où il dit l'inverse.
  if (!data || data.total === 0) {
    return null;
  }

  const jours = data.jours_avant_cloture;
  const variant = jours <= SEUIL_CRITIQUE ? "danger" : jours <= SEUIL_ATTENTION ? "warning" : "secondary";

  return (
    <Card className={`border-${variant}`}>
      <Card.Header className={`bg-${variant} text-white d-flex justify-content-between align-items-center`}>
        <span className="fw-bold">
          {data.total} manutention{data.total > 1 ? "s" : ""} à confirmer
        </span>
        <Badge bg="light" text="dark">
          {jours === 0 ? "Dernier jour du mois" : `${jours} jour${jours > 1 ? "s" : ""} avant la clôture`}
        </Badge>
      </Card.Header>

      <Card.Body className="pb-2">
        <p className="text-muted mb-3">
          Leur supplément vaut <strong>0 €</strong> tant qu'il n'est pas chiffré. Une fois la facture
          mensuelle émise, la confirmation n'est plus possible et la prestation est offerte.
        </p>

        <Table responsive hover size="sm" className="mb-0 align-middle">
          <thead>
            <tr>
              <th>Commande</th>
              <th>Client</th>
              <th>Livreur</th>
              <th>Livraison</th>
              <th>Ce que le client a indiqué</th>
              <th className="text-end">Action</th>
            </tr>
          </thead>
          <tbody>
            {data.commandes.map((c) => (
              <tr key={c.id}>
                <td className="fw-semibold">#{c.id}</td>
                <td>
                  <div>{c.client}</div>
                  {c.telephone && <small className="text-muted">{c.telephone}</small>}
                </td>
                <td>
                  {/* Sans livreur assigné, la fiche manutention de 17 h ne
                      couvre pas cette commande : elle ne partira à personne, et
                      seul un appel au client peut la débloquer. */}
                  {c.livreur || <span className="text-muted">non assigné</span>}
                </td>
                <td>
                  {c.date_livraison || "—"}
                  {/* « il y a N j » s'affichait aussi sur les livraisons à
                      venir : le calcul rendait une valeur absolue, si bien
                      qu'une livraison de demain se lisait comme une attente
                      d'un jour. Avant la livraison, il n'y a rien à constater
                      et donc rien à relancer. */}
                  {c.pas_encore_livree ? (
                    <div>
                      <small className="text-muted">pas encore livré</small>
                    </div>
                  ) : c.jours_attente > 0 && (
                    <div>
                      <small className="text-muted">il y a {c.jours_attente} j</small>
                    </div>
                  )}
                </td>
                <td>
                  <small>{resumerReponses(c.reponses) || <span className="text-muted">aucune réponse</span>}</small>
                  {c.reponses?.description_acces && (
                    <div>
                      <small className="text-muted fst-italic">{c.reponses.description_acces}</small>
                    </div>
                  )}
                  {/* Une commande encore à confirmer mais déjà passée par le
                      livreur est le cas que le barème refuse de tarifer : les
                      constats sont là, seul le montant négocié manque. */}
                  {c.provenance?.source === "livreur" && (
                    <div>
                      <small className="text-success">
                        Constaté par le livreur — accès hors barème, saisir un montant négocié
                      </small>
                    </div>
                  )}
                </td>
                <td className="text-end">
                  {/* /edit/ et non /show/ : le formulaire de confirmation vit
                      sur l'écran d'édition. Le lien menait jusqu'ici à la fiche,
                      qui ne fait qu'afficher — l'action annoncée par l'infobulle
                      n'y était pas possible. */}
                  <a
                    href={`/commande-fournisseur/edit/${c.id}`}
                    className="btn btn-sm btn-outline-primary p-1"
                    title="Ouvrir la commande pour confirmer"
                  >
                    <img src={IconShow} width="16px" alt="Voir" />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

export default ManutentionsAConfirmer;
